import { green } from '@ant-design/colors';
import { FileTextOutlined, SignatureFilled } from '@ant-design/icons';
import { type DettaglioFirma } from '@framework/api-client/api/inquiries-compile/schemas/dettaglioFirma.ts';
import { ModalDialog } from '@framework/ui/components/modal/ModalDialog.tsx';
import {
  Button,
  type ButtonProps,
  Flex,
  Tooltip,
  type TooltipProps,
  Typography,
  type UploadFile,
} from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { formatFileSize } from './formatFileSize.ts';
import { SignatureBlock } from './SignatureBlock.tsx';

const { Text } = Typography;

const SignatureModalDialog = styled(ModalDialog)`
  .ant-modal-content {
    padding: 0;
    border-radius: 8px;
  }
  .ant-modal-footer {
    padding: 12px 16px;
    padding-top: 0;
  }
`;
const SignatureModalHeader = styled(Flex)`
  background-color: ${props => props.theme.Layout?.bodyBg ?? props.theme.colorBgLayout};
  border-bottom: 1px solid ${props => props.theme.colorSplit};
  padding: 12px 16px;
  /* padding-right maggiore per fare spazio pulsante chiusura */
  padding-right: 40px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;
const SignatureModalContent = styled.div`
  padding: 16px;
  & + & {
    border-top: 1px solid ${props => props.theme.colorSplit};
  }
`;

const FileIcon = styled(FileTextOutlined)`
  font-size: 24px;
  color: ${props => props.theme.colorTextSecondary};
`;

const SignatureIcon = styled(SignatureFilled)`
  color: ${green[6]};
`;

const FileName = styled(Text)`
  font-size: 16px;
  line-height: 1.2;
  font-weight: 600;
  display: block;
  margin-bottom: 3px;
`;

const FileSize = styled(Text)`
  display: block;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  font-family: ${props => props.theme.fontFamilyCode || 'monospace'};
  color: ${props => props.theme.colorTextSecondary};
`;

export interface ISignatureModalButtonProps
  extends Omit<ButtonProps, 'onClick'> {
  /** File firmato */
  signedFile?: UploadFile | null;
  /** Dati di firma, se disponibili */
  signatures?: DettaglioFirma[];
  /** Props per il Tooltip che avvolge il pulsante */
  tooltipProps?: TooltipProps;
}

/**
 * Pulsante che apre una modal con il riepilogo dei dati di firma di un documento
 * (header con file + lista di `SignatureBlock` per ogni firma presente).
 */
export function SignatureModalButton(
  props: Readonly<ISignatureModalButtonProps>,
) {
  const { signedFile, signatures, tooltipProps, ...buttonProps } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();
  if (!signedFile) return null;
  if (!signatures?.length) return null;

  const fileName = signedFile.name;
  const fileSize =
    signedFile.size != null ? formatFileSize(signedFile.size) : null;

  return (
    <>
      <Tooltip
        title={t('signer.verifica-firma', 'Verifica Firma')}
        {...tooltipProps}
      >
        <Button
          icon={<SignatureIcon aria-hidden="true" />}
          aria-label={t('signer.verifica-firma', 'Verifica Firma')}
          type="link"
          size="middle"
          {...buttonProps}
          onClick={() => setModalOpen(true)}
        />
      </Tooltip>

      <SignatureModalDialog
        open={modalOpen}
        title={
          <span className="sr-only">
            {t('signer.verifica-firma', 'Verifica Firma')}
          </span>
        }
        onCancel={() => setModalOpen(false)}
        size="small"
        width={450}
        footer={null}
        destroyOnClose
      >
        <SignatureModalHeader>
          <Flex align="center" gap={12}>
            <FileIcon aria-hidden="true" />
            <Flex vertical>
              <FileName>{fileName}</FileName>
              {fileSize && <FileSize>{fileSize}</FileSize>}
            </Flex>
          </Flex>
        </SignatureModalHeader>

        {signatures.map((signature, index) => (
          <SignatureModalContent
            key={`${signature.codiceFiscale ?? ''}-${signature.data ?? ''}-${signature.formato ?? ''}-${index}`}
          >
            <SignatureBlock signature={signature} />
          </SignatureModalContent>
        ))}
      </SignatureModalDialog>
    </>
  );
}
